import type { RouteLocationRaw } from '#vue-router'
import type { InferOutput } from 'valibot'
import { array, nullish, object, pipe, string, transform, union } from 'valibot'
import { toTemplateRoutePath } from '~/utils/templates/template-files'

/**
 * Minimal schema for law documents in favorites
 * Contains only the properties needed for display in the workspace
 */
export const workspaceLawSchema = pipe(
  object({
    id: string(),
    title: nullish(string()),
    shortTitle: nullish(string()),
    dateInForce: nullish(string()),
    nodeType: nullish(string()),
    lawId: nullish(string()),
    lawFragmentId: nullish(string()),
  }),
  transform(law => ({
    ...law,
    displayTitle: law.title || law.shortTitle || 'Uten tittel',
    displayDate: law.dateInForce ? new Date(law.dateInForce).getFullYear().toString() : 'Ukjent dato',
  })),
)

export type WorkspaceLaw = InferOutput<typeof workspaceLawSchema>

/**
 * Minimal schema for book documents in favorites
 * Contains only the properties needed for display in the workspace
 */
export const workspaceBookSchema = pipe(
  object({
    id: string(),
    title: string(),
    author: nullish(array(string())),
    authorName: nullish(array(string())),
    year: nullish(string()),
    nodeType: nullish(string()),
    remoteId: nullish(string()),
    dateOfPublication: nullish(string()),
    lastUpdatedByEditor: nullish(array(string())),
  }),
  transform((book) => {
    const authors = (book.author && book.author.length > 0)
      ? book.author
      : (book.authorName ?? [])
    const lastUpdated = book.lastUpdatedByEditor?.[0]
    const sortableDate = lastUpdated || book.dateOfPublication || book.year || undefined
    const isAcademicArticle = book.nodeType === 'academic_article'

    return {
      ...book,
      sortableDate,
      displayTitle: book.title || 'Uten tittel',
      displayAuthor: authors.length > 0 ? `Av ${authors.join(', ')}` : '',
      displayDate: sortableDate
        ? (isAcademicArticle ? formatDate(sortableDate) : formatDate(sortableDate, { year: 'numeric' }))
        : 'Ukjent dato',
    }
  }),
)

export type WorkspaceBook = InferOutput<typeof workspaceBookSchema>

/**
 * Minimal schema for journal article documents in favorites
 * Contains only the properties needed for display in the workspace
 */
export const workspaceJournalSchema = pipe(
  object({
    id: string(),
    title: nullish(string()),
    author: nullish(union([string(), array(string())])),
    publish_date: nullish(union([string(), array(string())])),
    publisherId: nullish(string()),
    volume: nullish(string()),
    issue: nullish(string()),
  }),
  transform((journal) => {
    const author = Array.isArray(journal.author) ? journal.author.join(', ') : journal.author
    const publishDate = Array.isArray(journal.publish_date) ? journal.publish_date[0] : journal.publish_date
    const publishYear = publishDate && !Number.isNaN(new Date(publishDate).getTime())
      ? new Date(publishDate).getFullYear().toString()
      : undefined

    return {
      ...journal,
      displayTitle: journal.title || 'Uten tittel',
      displayAuthor: author ? `Av ${author}` : '',
      displayDate: publishYear || 'Ukjent dato',
    }
  }),
)

export type WorkspaceJournal = InferOutput<typeof workspaceJournalSchema>

/**
 * Minimal schema for template documents in favorites
 * Contains only the properties needed for display in the workspace
 */
export const workspaceTemplateSchema = pipe(
  object({
    id: string(),
    title: string(),
    authorName: nullish(array(string())),
    lastUpdatedByEditor: nullish(array(string())),
    alias: nullish(string()),
  }),
  transform(template => ({
    ...template,
    displayTitle: template.title || 'Uten tittel',
    displayAuthor: template.authorName && template.authorName.length > 0 ? `${template.authorName.join(', ')}` : '',
    displayDate: template.lastUpdatedByEditor && template.lastUpdatedByEditor.length > 0
      ? `Sist oppdatert ${formatDate(template.lastUpdatedByEditor[0], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}`
      : 'Ukjent dato',
  })),
)

export type WorkspaceTemplate = InferOutput<typeof workspaceTemplateSchema>

/**
 * Type for the unified favorite item structure used in the frontend
 */
export interface FavoriteDisplayItem {
  /** Optional badge shown in the middle column — used by the Markeringer view to show "X markeringer". */
  countLabel?: string
  id: string
  title: string
  author: string
  date: string
  sortDate?: string
  type: 'lov' | 'faglitteratur' | 'tidsskrift' | 'mal'
  url?: RouteLocationRaw | string
}

/**
 * Transform law item to frontend display format
 */
export function mapLawToDisplayItem(law: NonNullable<WorkspaceLaw>): FavoriteDisplayItem {
  // For legal_commentary documents, use lawId + lawFragmentId to build URL
  // For law documents, use the id directly
  let url: RouteLocationRaw
  if (law.nodeType === 'legal_commentary' && law.lawId) {
    const lovId = [
      law.lawId,
      ...(law.lawFragmentId ? law.lawFragmentId.split('/').filter(Boolean) : []),
    ]
    url = { name: 'law-by-id', params: { lovId } } as RouteLocationRaw
  }
  else {
    url = { name: 'law-by-id', params: { lovId: [law.id] } } as RouteLocationRaw
  }

  return {
    id: law.id,
    title: law.displayTitle,
    author: '', // Law items don't have author
    date: law.displayDate,
    sortDate: law.dateInForce ?? undefined,
    type: 'lov',
    url,
  }
}

/**
 * Transform book item to frontend display format
 */
export function mapBookToDisplayItem(book: NonNullable<WorkspaceBook>): FavoriteDisplayItem {
  const isAcademicArticle = book.nodeType === 'academic_article'
  let url: RouteLocationRaw | undefined
  if (isAcademicArticle) {
    if (book.remoteId) {
      url = { name: 'academic-article-by-id', params: { artikkelId: [book.remoteId] } } as RouteLocationRaw
    }
    else {
      console.warn(`[favorites] Academic article ${book.id} is missing remoteId — link will be unavailable`)
    }
  }
  else {
    url = { name: 'book-by-id', params: { bokId: [book.id] } } as RouteLocationRaw
  }

  return {
    id: book.id,
    title: book.displayTitle,
    author: book.displayAuthor,
    date: book.displayDate,
    sortDate: book.sortableDate,
    type: 'faglitteratur',
    url,
  }
}

/**
 * Transform journal item to frontend display format
 */
export function mapJournalToDisplayItem(journal: NonNullable<WorkspaceJournal>): FavoriteDisplayItem {
  const articleNumber = journal.id.split('/').filter(Boolean).pop()
  const normalizedIssue = journal.issue?.startsWith('utgave-')
    ? journal.issue.replace('utgave-', '')
    : journal.issue

  const url: RouteLocationRaw | undefined = journal.publisherId && journal.volume && normalizedIssue && articleNumber
    ? {
        name: 'journal-article-volume',
        params: {
          publisherId: journal.publisherId,
          volume: journal.volume,
          issue: normalizedIssue,
          articleNumber,
        },
      } as RouteLocationRaw
    : undefined

  const rawPublishDate = Array.isArray(journal.publish_date) ? journal.publish_date[0] : journal.publish_date

  return {
    id: journal.id,
    title: journal.displayTitle,
    author: journal.displayAuthor,
    date: journal.displayDate,
    sortDate: rawPublishDate ?? undefined,
    type: 'tidsskrift',
    url,
  }
}

/**
 * Transform template item to frontend display format
 */
export function mapTemplateToDisplayItem(template: NonNullable<WorkspaceTemplate>): FavoriteDisplayItem {
  return {
    id: template.id,
    title: template.displayTitle,
    author: template.displayAuthor,
    date: template.displayDate,
    sortDate: template.lastUpdatedByEditor?.[0] ?? undefined,
    type: 'mal',
    url: toTemplateRoutePath(template),
  }
}
